import type { ProductFragment, ProductVariantFragment } from '@/types/shopify/storefront'
import type { CartWithActions } from '@shopify/hydrogen-react'
import type { CartLine, CurrencyCode } from '@shopify/hydrogen-react/storefront-api-types'
import type { StorefrontCart, StorefrontCartLine, StorefrontProduct, StorefrontVariant } from 'analyzify-js'
import type { BaseHit, Hit } from 'instantsearch.js'

/** A price resolved outside Shopify - currently the LPG location tier from the BFF. */
export type PriceOverride = { amount: number; currencyCode?: CurrencyCode }

const toMoney = (amount: string, currencyCode: CurrencyCode) => ({ amount, currencyCode })

const toStorefrontVariant = (variant: ProductVariantFragment): StorefrontVariant => ({
  id: variant.id,
  title: variant.title,
  price: toMoney(variant.price.amount, variant.price.currencyCode),
  compareAtPrice: variant.compareAtPrice ? toMoney(variant.compareAtPrice.amount, variant.compareAtPrice.currencyCode) : null,
  availableForSale: variant.availableForSale,
  sku: variant.sku ?? undefined,
  selectedOptions: variant.selectedOptions.map(({ name, value }) => ({ name, value })),
  image: variant.image ? { url: variant.image.url, altText: variant.image.altText ?? undefined } : undefined,
})

/** Smallest and largest values in a list, as Storefront money objects. */
const priceRangeFrom = (amounts: number[], currencyCode: CurrencyCode) => {
  if (amounts.length === 0) return undefined

  return {
    minVariantPrice: toMoney(Math.min(...amounts).toString(), currencyCode),
    maxVariantPrice: toMoney(Math.max(...amounts).toString(), currencyCode),
  }
}

export const toStorefrontProduct = (product: ProductFragment, priceOverride?: PriceOverride): StorefrontProduct => {
  const variants = product.variants.nodes
  const currencyCode = priceOverride?.currencyCode ?? variants[0]?.price.currencyCode ?? 'AUD'

  // The SDK reads GA4's `value` from the variant price, not the product range,
  // so an override has to reach the variants to have any effect.
  const overridden = priceOverride ? variants.map((variant) => ({ ...variant, price: toMoney(priceOverride.amount.toString(), currencyCode) })) : variants

  const prices = overridden.map((variant) => parseFloat(variant.price.amount)).filter((amount) => !isNaN(amount))

  // Only variants actually on sale carry a compareAtPrice, so this list is
  // often shorter than `prices` - and empty when nothing is discounted.
  const compareAtPrices = overridden.map((variant) => parseFloat(variant.compareAtPrice?.amount ?? '')).filter((amount) => !isNaN(amount))

  const images = product.images.nodes

  return {
    id: product.id,
    title: product.title,
    handle: product.handle,
    vendor: product.vendor,
    productType: product.productType,
    tags: product.tags,
    availableForSale: product.availableForSale,
    description: product.description,
    priceRange: priceRangeFrom(prices, currencyCode) ?? {
      minVariantPrice: toMoney('0', currencyCode),
      maxVariantPrice: toMoney('0', currencyCode),
    },
    compareAtPriceRange: priceRangeFrom(compareAtPrices, currencyCode),
    featuredImage: images[0] ? { url: images[0].url } : undefined,
    images: { edges: images.map((image) => ({ node: { url: image.url } })) },
    variants: { edges: overridden.map((variant) => ({ node: toStorefrontVariant(variant) })) },
  }
}

/** The price the customer actually pays per unit, after Cart Transform. */
const unitPriceOf = (line: CartLine) => {
  const perQuantity = line.cost?.amountPerQuantity?.amount
  if (perQuantity) return perQuantity

  // Older lines may not carry amountPerQuantity - derive it from the total.
  const total = parseFloat(line.cost?.totalAmount?.amount ?? '')
  const quantity = line.quantity || 0
  if (!isNaN(total) && quantity > 0) return (total / quantity).toString()

  return line.merchandise?.price?.amount ?? '0'
}

const toStorefrontCartLine = (line: CartLine): StorefrontCartLine => {
  const merchandise = line.merchandise
  const product = merchandise?.product
  const currencyCode = line.cost?.totalAmount?.currencyCode ?? merchandise?.price?.currencyCode ?? 'AUD'

  return {
    id: line.id,
    quantity: line.quantity ?? 0,
    attributes: (line.attributes ?? []).flatMap((attribute) => (attribute?.key ? [{ key: attribute.key, value: attribute.value ?? '' }] : [])),
    merchandise: {
      id: merchandise?.id ?? '',
      title: merchandise?.title ?? '',
      price: toMoney(unitPriceOf(line), currencyCode),
      compareAtPrice: merchandise?.compareAtPrice ? toMoney(merchandise.compareAtPrice.amount, merchandise.compareAtPrice.currencyCode) : null,
      sku: merchandise?.sku ?? undefined,
      image: merchandise?.image ? { url: merchandise.image.url } : undefined,
      product: {
        id: product?.id ?? '',
        title: product?.title ?? '',
        handle: product?.handle ?? '',
        vendor: product?.vendor ?? '',
        productType: product?.productType ?? '',
      },
    },
  }
}

export const toStorefrontCart = (cart: CartWithActions): StorefrontCart => {
  const lines = (cart.lines ?? []) as CartLine[]
  const currencyCode = cart.cost?.totalAmount?.currencyCode ?? 'AUD'

  return {
    id: cart.id ?? '',
    checkoutUrl: cart.checkoutUrl ?? '',
    totalQuantity: cart.totalQuantity ?? 0,
    // Neither is exposed by useCart(); the SDK only passes them through.
    createdAt: '',
    updatedAt: '',
    cost: {
      totalAmount: toMoney(cart.cost?.totalAmount?.amount ?? '0', currencyCode),
      subtotalAmount: toMoney(cart.cost?.subtotalAmount?.amount ?? '0', currencyCode),
      totalTaxAmount: cart.cost?.totalTaxAmount ? toMoney(cart.cost.totalTaxAmount.amount ?? '0', currencyCode) : undefined,
    },
    lines: { edges: lines.map((line) => ({ node: toStorefrontCartLine(line) })) },
    attributes: (cart.attributes ?? []).flatMap((attribute) => (attribute?.key ? [{ key: attribute.key, value: attribute.value ?? '' }] : [])),
    buyerIdentity: cart.buyerIdentity
      ? {
          email: cart.buyerIdentity.email ?? undefined,
          phone: cart.buyerIdentity.phone ?? undefined,
          customer: cart.buyerIdentity.customer?.id ? { id: cart.buyerIdentity.customer.id } : undefined,
        }
      : undefined,
  }
}

/**
 * Bridges an Algolia hit onto the SDK's product shape.
 */
const AUD: CurrencyCode = 'AUD'

/** Algolia gives bare numeric ids; the SDK expects Storefront GIDs. */
const toGid = (resource: 'Product' | 'ProductVariant', id: string | number) => `gid://shopify/${resource}/${id}`

const money = (value: number) => toMoney(value.toString(), AUD)

export const toStorefrontProductFromHit = (hit: Hit<BaseHit>, priceOverride?: PriceOverride): StorefrontProduct => {
  const hitPrice = typeof hit.price === 'number' ? hit.price : 0
  const price = priceOverride?.amount ?? hitPrice
  const minPrice = priceOverride ? price : typeof hit.variants_min_price === 'number' ? hit.variants_min_price : price
  const maxPrice = priceOverride ? price : typeof hit.variants_max_price === 'number' ? hit.variants_max_price : price
  const compareMin = priceOverride ? null : typeof hit.variants_compare_at_price_min === 'number' ? hit.variants_compare_at_price_min : null
  const compareMax = typeof hit.variants_compare_at_price_max === 'number' ? hit.variants_compare_at_price_max : compareMin

  const image = typeof hit.image === 'string' ? hit.image : undefined
  const available = hit.inventory_available === true

  // Algolia indexes one record per variant, so a hit only ever describes one.
  const variant: StorefrontVariant = {
    id: toGid('ProductVariant', String(hit.objectID)),
    title: typeof hit.variant_title === 'string' ? hit.variant_title : '',
    price: money(price),
    compareAtPrice: compareMin ? money(compareMin) : null,
    availableForSale: available,
    sku: typeof hit.sku === 'string' ? hit.sku : undefined,
    selectedOptions: [hit.option1, hit.option2, hit.option3]
      .filter((value): value is string => typeof value === 'string' && value.length > 0)
      .map((value, index) => ({ name: `option${index + 1}`, value })),
    image: image ? { url: image } : undefined,
  }

  return {
    id: toGid('Product', String(hit.id)),
    title: typeof hit.title === 'string' ? hit.title : '',
    handle: typeof hit.handle === 'string' ? hit.handle : '',
    vendor: typeof hit.vendor === 'string' ? hit.vendor : '',
    productType: typeof hit.product_type === 'string' ? hit.product_type : '',
    tags: Array.isArray(hit.tags) ? hit.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    availableForSale: available,
    priceRange: { minVariantPrice: money(minPrice), maxVariantPrice: money(maxPrice) },
    compareAtPriceRange: compareMin ? { minVariantPrice: money(compareMin), maxVariantPrice: money(compareMax ?? compareMin) } : undefined,
    featuredImage: image ? { url: image } : undefined,
    images: image ? { edges: [{ node: { url: image } }] } : undefined,
    variants: { edges: [{ node: variant }] },
  }
}

/**
 * Builds a product from a cart line, for add_to_cart / remove_from_cart.
 *
 * track.addToCart takes a product, but a cart line only carries the merchandise
 * subset of one. That is enough: the SDK reads id, title, vendor, productType
 * and the variant. Price uses the Cart Transform value for the same reason as
 * toStorefrontCart - it is what the customer actually pays.
 */
export const toStorefrontProductFromCartLine = (line: CartLine): StorefrontProduct => {
  const merchandise = line.merchandise
  const product = merchandise?.product
  const currencyCode = line.cost?.totalAmount?.currencyCode ?? merchandise?.price?.currencyCode ?? AUD
  const price = toMoney(unitPriceOf(line), currencyCode)

  const variant: StorefrontVariant = {
    id: merchandise?.id ?? '',
    title: merchandise?.title ?? '',
    price,
    compareAtPrice: merchandise?.compareAtPrice ? toMoney(merchandise.compareAtPrice.amount, currencyCode) : null,
    availableForSale: merchandise?.availableForSale ?? true,
    sku: merchandise?.sku ?? undefined,
    selectedOptions: (merchandise?.selectedOptions ?? []).flatMap((option) => (option?.name ? [{ name: option.name, value: option.value ?? '' }] : [])),
    image: merchandise?.image ? { url: merchandise.image.url } : undefined,
  }

  return {
    id: product?.id ?? '',
    title: product?.title ?? '',
    handle: product?.handle ?? '',
    vendor: product?.vendor ?? '',
    productType: product?.productType ?? '',
    tags: [],
    availableForSale: variant.availableForSale,
    priceRange: { minVariantPrice: price, maxVariantPrice: price },
    featuredImage: variant.image,
    images: variant.image ? { edges: [{ node: variant.image }] } : undefined,
    variants: { edges: [{ node: variant }] },
  }
}
